import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { SURFACE_PAGE_TITLE } from '@/shared/ui-class';
import type { CapacityStrategyId, MetalPriceState, OreGradeState } from '@/versions/v1/schema/capacity';
import { computeCapacityStrategies } from '@/versions/v1/shared/CapacityStrategy';
import { evaluateCapacity } from '@/versions/v1/shared/CapacityEngine';
import { CAPACITY_SEED, STRATEGY_PRICE_LIMITS } from '@/versions/v1/shared/capacity-seed';
import { defaultSmelterPriceState } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { GradeStrip } from './GradeStrip';
import { StrategyTab } from './StrategyTab';
import { effectiveStrategySelection } from './StrategyTab.transform';
import { ThroughputTab } from './ThroughputTab';
import {
  capacityPlantState,
  capacityHeaderKpis,
  capacityTabFromSearch,
  capacityWhatIf,
  CAPACITY_TABS,
  type CapacityTabId,
} from './Scene.transform';

const KPI_TONE = {
  ok: 'text-[var(--state-online)]',
  warn: 'text-[var(--state-warning)]',
  bad: 'text-[var(--state-blocked)]',
  neutral: 'text-ink-primary',
} as const;

export function CapacityScene() {
  const { projectId = 'alpha' } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const { state: sessionState } = useScenario();
  const [tab, setTab] = useState<CapacityTabId>('0');
  const [gradeOverride, setGradeOverride] = useState<OreGradeState | null>(null);
  const deck = useMemo<MetalPriceState>(() => ({ ...defaultSmelterPriceState() }), []);
  const [prices, setPrices] = useState<MetalPriceState>(() => ({ ...defaultSmelterPriceState() }));
  const [manualStrategy, setManualStrategy] = useState<CapacityStrategyId | null>(null);
  const projectTitle = 'Capacity & strategy';
  const plantState = useMemo(() => capacityPlantState(sessionState), [sessionState]);
  const grade = gradeOverride ?? plantState.grade;
  const read = useMemo(
    () => evaluateCapacity({ grade, seed: CAPACITY_SEED, prices, plantState }),
    [grade, prices, plantState],
  );
  const strategyRead = useMemo(
    () => computeCapacityStrategies({ grade, prices, plantState }),
    [grade, prices, plantState],
  );
  // C's Ag line: the same strategies at the silver slider's two ends.
  const agEnds = useMemo(
    () => ({
      low: computeCapacityStrategies({ grade, prices: { ...prices, silverPerOz: STRATEGY_PRICE_LIMITS.silverMin }, plantState }),
      high: computeCapacityStrategies({ grade, prices: { ...prices, silverPerOz: STRATEGY_PRICE_LIMITS.silverMax }, plantState }),
    }),
    [grade, prices, plantState],
  );
  const selectedStrategy = effectiveStrategySelection(strategyRead.recommendedId, manualStrategy);
  const headerKpis = capacityHeaderKpis(read);
  const whatIf = capacityWhatIf(grade, plantState.grade, prices, deck);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    setTab(capacityTabFromSearch(tabParam));
  }, [searchParams]);

  useEffect(() => {
    document.title = `${projectTitle} · Bunker Hill`;
  }, []);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="CapacityStrategy">
        <SurfacePageBody className="space-y-3" width="full">
          <header className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <h1 className={cn(SURFACE_PAGE_TITLE, 'min-w-0')}>{projectTitle}</h1>
            {whatIf.active ? (
              <>
                <span
                  data-what-if
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--ink-tertiary)] bg-[var(--surface-2)] py-0.5 pl-2 pr-2.5 text-[10px] font-semibold uppercase tracking-[0.05em] tabular-nums text-ink-primary"
                >
                  <i aria-hidden className="h-1.5 w-1.5 rounded-full bg-[var(--state-warning)]" />
                  {whatIf.label}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setGradeOverride(null);
                    setPrices({ ...deck });
                  }}
                  className="h-7 rounded-[9px] border border-[var(--hairline-strong)] px-2.5 text-xs font-semibold text-ink-secondary transition-colors hover:bg-[var(--surface-1)] hover:text-ink-primary"
                >
                  Reset to measured
                </button>
              </>
            ) : null}
            <div className="ml-auto flex items-center gap-3">
              <dl className="flex flex-wrap items-start tabular-nums" aria-label="Plant at the cursor">
                {headerKpis.map((kpi, index) => (
                  <div
                    key={kpi.id}
                    className={cn(
                      'min-w-0 px-3.5',
                      index === headerKpis.length - 1 ? 'pr-0' : 'border-r border-[var(--color-divider)]',
                    )}
                  >
                    <dt className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{kpi.label}</dt>
                    <dd className={cn('text-lg font-semibold leading-snug', KPI_TONE[kpi.tone])}>
                      {kpi.value}
                      {kpi.unit ? <span className="ml-1 text-xs font-medium text-muted">{kpi.unit}</span> : null}
                    </dd>
                    {kpi.sub ? <dd className="text-xs text-muted">{kpi.sub}</dd> : null}
                  </div>
                ))}
              </dl>
              <HelpAnchor doc="ingestion/capacity-strategy" label="Capacity help" />
            </div>
          </header>

          <div
            role="tablist"
            aria-label="Capacity & strategy"
            className="flex flex-wrap items-end gap-0.5 border-b border-[var(--hairline-strong)]"
          >
            {CAPACITY_TABS.map((item) => (
              <button
                key={item.id}
                id={`capacity-tab-${item.id}`}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                aria-controls={`capacity-panel-${item.id}`}
                disabled={item.disabled}
                onClick={() => {
                  if (!item.disabled) setTab(item.id);
                }}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  tab === item.id && !item.disabled
                    ? 'border-[var(--ink-primary)] text-ink-primary'
                    : 'border-transparent text-muted hover:text-ink-primary',
                  item.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          <GradeStrip grade={grade} measured={plantState.grade} read={read} onChange={setGradeOverride} />

          {tab === '0' ? (
            <div id="capacity-panel-0" role="tabpanel" aria-labelledby="capacity-tab-0" className="min-w-0">
              <ThroughputTab read={read} projectId={projectId} />
            </div>
          ) : null}
          {tab === '1' ? (
            <div id="capacity-panel-1" role="tabpanel" aria-labelledby="capacity-tab-1" className="min-w-0">
              <StrategyTab
                projectId={projectId}
                prices={prices}
                deck={deck}
                read={strategyRead}
                agEnds={agEnds}
                feedTag={read.sourceTags.currentFeed}
                selectedId={selectedStrategy}
                onPricesChange={setPrices}
                onStrategyChange={setManualStrategy}
              />
            </div>
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
